import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { AgentJob, AgentStatus } from "@/lib/types";

export function useItemJob(itemId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["agent-job", itemId],
    queryFn: async () => {
      if (!user || !itemId) return null;

      const { data, error } = await supabase
        .from("agent_jobs")
        .select("*")
        .eq("item_id", itemId)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as AgentJob | null;
    },
    enabled: !!user && !!itemId,
    refetchInterval: 10_000,
  });
}

export function useActiveJobs() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["active-jobs", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("agent_jobs")
        .select("*")
        .eq("user_id", user.id)
        .in("status", ["pending", "claimed", "running"])
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as AgentJob[];
    },
    enabled: !!user,
    refetchInterval: 10_000,
  });
}

export function useDispatchJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ item_id, instructions }: { item_id: string; instructions?: string }) => {
      const { data, error } = await supabase.functions.invoke("dispatch-job", {
        body: { item_id, instructions },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-job"] });
      queryClient.invalidateQueries({ queryKey: ["active-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

export function useRecentJobs() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["recent-jobs", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("agent_jobs")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      return data as AgentJob[];
    },
    enabled: !!user,
    refetchInterval: 15_000,
  });
}

export function useRetryJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (job: AgentJob) => {
      // Re-dispatch the same item
      const { data, error } = await supabase.functions.invoke("dispatch-job", {
        body: { item_id: job.item_id, instructions: job.instructions || undefined },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recent-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["active-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["agent-job"] });
    },
  });
}

export function useDismissJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await supabase
        .from("agent_jobs")
        .update({ status: "cancelled", error: "Dismissed by user" })
        .eq("id", jobId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recent-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["active-jobs"] });
    },
  });
}

export function useAgentStatus() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["agent-status", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("agents")
        .select("*")
        .eq("user_id", user.id);

      if (error) throw error;
      return (data ?? []) as AgentStatus[];
    },
    enabled: !!user,
    refetchInterval: 30_000,
  });
}
